import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard,
  Users,
  Settings as SettingsIcon,
  LogOut,
  Menu,
  PlusCircle,
  ScanLine,
  KeyRound,
  Sticker,
} from "lucide-react";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/kunden", label: "Kunden", icon: Users, testId: "nav-customers" },
  { to: "/aufsteller", label: "Aufsteller", icon: Sticker, testId: "nav-stands" },
  { to: "/kunden-zugaenge", label: "Kunden-Zugänge", icon: KeyRound, testId: "nav-portal-users" },
  { to: "/einstellungen", label: "Einstellungen", icon: SettingsIcon, testId: "nav-settings" },
];

function NavItems({ onNavigate }) {
  return (
    <nav className="flex flex-col gap-1">
      {NAV.map(({ to, label, icon: Icon, testId }) => (
        <NavLink
          key={to}
          to={to}
          onClick={onNavigate}
          data-testid={testId}
          className={({ isActive }) =>
            cn(
              "group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors",
              "hover:bg-white/[0.04]",
              isActive
                ? "bg-primary/10 text-primary border border-primary/20"
                : "text-muted-foreground border border-transparent"
            )
          }
        >
          <Icon className="w-4 h-4" />
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

function BrandMark() {
  return (
    <div className="flex items-center gap-2.5">
      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-blue-500 grid place-items-center shadow-lg shadow-primary/20">
        <ScanLine className="w-4 h-4 text-primary-foreground" />
      </div>
      <div className="leading-none">
        <div className="font-heading font-bold text-lg tracking-tight">1AI Review</div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-0.5">
          NFC & QR Manager
        </div>
      </div>
    </div>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-white/[0.06] bg-card/60 backdrop-blur-xl px-4 py-6">
        <div className="px-1">
          <BrandMark />
        </div>
        <div className="mt-8 flex-1">
          <NavItems />
        </div>
        <div className="mt-6 border-t border-white/[0.06] pt-4">
          <div className="px-3 py-2 text-xs text-muted-foreground truncate">
            {user?.email}
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground"
            onClick={handleLogout}
            data-testid="nav-logout"
          >
            <LogOut className="w-4 h-4" /> Abmelden
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-40 bg-background/90 backdrop-blur-xl border-b border-white/[0.06] px-4 py-3 flex items-center justify-between">
        <BrandMark />
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="mobile-menu-toggle">
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-card border-white/[0.06] w-72">
            <div className="mb-6">
              <BrandMark />
            </div>
            <NavItems onNavigate={() => setMobileOpen(false)} />
            <div className="mt-6 border-t border-white/[0.06] pt-4">
              <div className="px-3 py-2 text-xs text-muted-foreground truncate">
                {user?.email}
              </div>
              <Button
                variant="ghost"
                className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground"
                onClick={() => {
                  setMobileOpen(false);
                  handleLogout();
                }}
                data-testid="mobile-nav-logout"
              >
                <LogOut className="w-4 h-4" /> Abmelden
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </header>

      {/* Main content */}
      <main className="lg:pl-64">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10 animate-fade-in">
          {children}
        </div>
      </main>

      {/* Floating "add customer" for mobile */}
      <Link
        to="/kunden/neu"
        data-testid="fab-new-customer"
        className="lg:hidden fixed bottom-5 right-5 bg-primary text-primary-foreground rounded-full h-14 px-5 shadow-2xl shadow-primary/30 flex items-center gap-2 font-medium active:scale-[0.98] transition-transform"
      >
        <PlusCircle className="w-5 h-5" />
        Neue Firma
      </Link>
    </div>
  );
}
